import { useEffect, useRef, useState } from "react";
import {
    getTooltipSnapshot,
    TooltipPlacement,
    TooltipSnapshot,
} from "../libs/favoriteMessageTooltip";

type Params = {
    open: boolean;
    anchorEl: HTMLElement | null;
    message: string;
    to?: string;
    placement: TooltipPlacement;
    autoHideMs: number;
    onClose: () => void;
};

type ReturnValue = {
    shouldRender: boolean;
    isExitAnimating: boolean;
    tooltipSnapshot: TooltipSnapshot;
    handleAnimationEnd: () => void;
};

/**
 * 終了アニメーション中も、閉じる直前のツールチップ内容を維持するフック。
 *
 * `open` が `false` になった瞬間にアンマウントすると、ユーザーが見ていた内容ではなく、
 * 次の props が一瞬反映されることがある。これを防ぐため、このフックは最後に表示していた
 * スナップショットを保持し、終了アニメーションが終わるまでその内容を描画し続ける。
 * あわせて、自動クローズ用のタイマーも管理し、ツールチップが表示中のときだけ有効にする。
 */
export const useTooltipExitWithSnapshot = ({
    open,
    anchorEl,
    message,
    to,
    placement,
    autoHideMs,
    onClose,
}: Params): ReturnValue => {
    const [shouldRender, setShouldRender] = useState(open);
    // 最後に表示していたスナップショットを保持する
    const lastOpenTooltipSnapshotRef = useRef<TooltipSnapshot | null>(null);
    // 現在のスナップショットを保持する
    const currentSnapshot: TooltipSnapshot = {
        anchorEl,
        message,
        to,
        placement,
    };

    /**
     * ツールチップが開いている間だけ最新の表示内容を保存する。
     * 閉じ始めた後は更新を止めることで、終了アニメーション中に見た目が切り替わらないようにする。
     */
    if (open) {
        lastOpenTooltipSnapshotRef.current = currentSnapshot;
    }

    const tooltipSnapshot = getTooltipSnapshot(
        open,
        lastOpenTooltipSnapshotRef.current,
        currentSnapshot
    );
    const isExitAnimating = !open && shouldRender;

    useEffect(() => {
        if (open) setShouldRender(true);
        if (!open || !shouldRender) return;
        const timeoutId = window.setTimeout(onClose, autoHideMs);
        return () => window.clearTimeout(timeoutId);
    }, [open, shouldRender, autoHideMs, onClose]);

    const handleAnimationEnd = () => {
        if (isExitAnimating) setShouldRender(false);
    };

    return {
        shouldRender,
        isExitAnimating,
        tooltipSnapshot,
        handleAnimationEnd,
    };
};
